import { mapKeys, mapValues } from 'lodash-es';
import type { CSSProperties } from 'react';

import { resolveMaybeFn } from '@infinex-private/ts-utils/function';
import type { Prettify } from '@infinex-private/ts-utils/types';
import { toPx } from '@infinex/ui-utils/dom';

type Definition = Record<string, `--${string}`>;
type Vars<T extends Record<string, string>> = {
  [K in keyof T]: `var(${T[K]})`;
};

/**
 * Create a strongly-typed object for managing and accessing the CSS variables
 * used by a component.
 */
export function componentVars<T extends Definition>(definition: T) {
  const mapped = mapValues(definition, (value) => `var(${value})`) as Prettify<
    Vars<T>
  >;

  type record = Record<keyof T, number | string>;
  return Object.assign(mapped, {
    apply(value: record | ((vars: typeof mapped) => record)): CSSProperties {
      const props = resolveMaybeFn(value, mapped);
      const keyed = mapKeys(props, (_value, key) => definition[key]);
      return mapValues(keyed, toPx);
    },
    get(key: keyof T) {
      return definition[key];
    },
    tw(...expressions: `${string}-(${T[keyof T]})${string}`[]) {
      return expressions.join(' ');
    },
  });
}
